import React from "react"

import { isSameDay } from "date-fns"
import { Calendar, CheckSquare, ChevronDown, Square, SquareX, User } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"

interface EventDetailsProps {
  isOpen: boolean
  onClose: () => void
  title: string
  event: {
    id: string
    date: Date
    startTime: string
    endTime: string
    location: string
    registered: number
    capacity: number
    jobPostings: string[]
  } | null
  attendees: Array<{
    id: string
    name: string
    email: string | null
    phone?: string | null
    position?: string | null
    attended: boolean
    orientationQuizResult?: number | null
  }>
  isLoadingAttendees?: boolean
  isEventInProgress: boolean
  onMarkAttendance: (attendeeId: string, reset?: boolean) => void
  onOpenReschedule: (attendeeId: string) => void
  renderEventDetails: () => React.ReactNode
  isOrientationEvent?: boolean
}

const EventDetailsDialog: React.FC<EventDetailsProps> = ({
  isOpen,
  onClose,
  title,
  event,
  attendees,
  isLoadingAttendees = false,
  isEventInProgress,
  onMarkAttendance,
  onOpenReschedule,
  renderEventDetails,
  isOrientationEvent = false,
}) => {
  // Check if event time has passed
  const hasEventPassed = (eventDate: Date, endTime: string): boolean => {
    const now = new Date()

    // If event date is in the past
    if (eventDate < now && !isSameDay(eventDate, now)) {
      return true
    }

    // If event is today, check if end time has passed
    if (isSameDay(eventDate, now)) {
      // Parse time with AM/PM format
      let hours = 0
      let minutes = 0

      // Check if time contains AM/PM
      if (endTime.includes("PM") || endTime.includes("AM")) {
        const isPM = endTime.includes("PM")
        const timePart = endTime.replace("PM", "").replace("AM", "").trim()
        const [hourStr, minuteStr] = timePart.split(":")

        hours = parseInt(hourStr, 10)
        minutes = parseInt(minuteStr, 10)

        // Convert to 24-hour format if PM
        if (isPM && hours < 12) {
          hours += 12
        }
        // Handle 12 AM special case
        if (!isPM && hours === 12) {
          hours = 0
        }
      } else {
        // Handle 24-hour format
        const [hourStr, minuteStr] = endTime.split(":")
        hours = parseInt(hourStr, 10)
        minutes = parseInt(minuteStr, 10)
      }

      const eventEndTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes,
      )

      return now > eventEndTime
    }

    return false
  }
  const navigate = useNavigate()

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {event && (
          <div className="space-y-6">
            {renderEventDetails()}

            <div>
              <h3 className="text-lg font-medium mb-2">Attendees</h3>
              {isLoadingAttendees ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-[200px]" />
                      <Skeleton className="h-4 w-[150px]" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-[200px]" />
                      <Skeleton className="h-4 w-[150px]" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-[200px]" />
                      <Skeleton className="h-4 w-[150px]" />
                    </div>
                  </div>
                </div>
              ) : attendees.length > 0 ? (
                <div className="border rounded-md divide-y">
                  {attendees.map((attendee) => (
                    <div key={attendee.id} className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User
                          className="h-5 w-5 text-palette-jungleGreen500 cursor-pointer hover:text-primary"
                          onClick={() => navigate(`/applications/${attendee.id}`)}
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{attendee.name}</p>
                            {isOrientationEvent && attendee.orientationQuizResult != null && (
                              <Badge variant="outline" className="text-xs">
                                Score: {attendee.orientationQuizResult}
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-2 items-center">
                            <p className="text-sm text-palette-jungleGreen500">
                              {attendee.email || "No email"}
                            </p>
                            {attendee.position && (
                              <Badge variant="outline" className="text-xs">
                                {attendee.position}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {isEventInProgress && (
                          <>
                            {attendee.attended === null ? (
                              // When attended is null, show a simple button to mark as attended
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onMarkAttendance(attendee.id)}
                                className="flex items-center gap-1"
                              >
                                <Square className="h-4 w-4" />
                                Mark Attended
                              </Button>
                            ) : attendee.attended === true ? (
                              // When attended is true, show split button with dropdown
                              <div className="flex">
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="rounded-r-none flex items-center gap-1 pointer-events-none"
                                >
                                  <CheckSquare className="h-4 w-4" />
                                  Attended
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="default"
                                      size="sm"
                                      className="rounded-l-none border-l border-l-primary-foreground/20 px-2"
                                    >
                                      <ChevronDown className="h-4 w-4 opacity-50" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem onClick={() => onMarkAttendance(attendee.id)}>
                                      Not Attended
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        // Reset to null
                                        onMarkAttendance(attendee.id, true)
                                      }}
                                    >
                                      Reset Value
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ) : (
                              // When attended is false, show split button with dropdown
                              <div className="flex">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-r-none flex items-center gap-1 bg-red-500 border-palette-jungleGreen500 text-white pointer-events-none"
                                >
                                  <SquareX className="h-3 w-3" />
                                  Not Attended
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="rounded-l-none border-palette-jungleGreen500 border-l-0"
                                    >
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem onClick={() => onMarkAttendance(attendee.id)}>
                                      Mark Attended
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        // Reset to null
                                        onMarkAttendance(attendee.id, true)
                                      }}
                                    >
                                      Reset Value
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            )}
                          </>
                        )}
                        {event && !hasEventPassed(event.date, event.endTime) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onOpenReschedule(attendee.id)}
                          >
                            <Calendar className="h-4 w-4 mr-1" /> Reschedule
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-palette-jungleGreen500">
                  No attendees registered for this event.
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default EventDetailsDialog
